import React, { useState, useEffect } from 'react';
import { 
  FiClock, 
  FiSearch, 
  FiFilter, 
  FiDownload, 
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiRefreshCw
} from 'react-icons/fi';
import { FaExchangeAlt, FaArrowUp, FaArrowDown, FaFileExcel, FaFilePdf, FaFileCsv } from 'react-icons/fa';
import './TransactionHistory.css';

interface Transaction {
  id: number;
  reference: string;
  type: 'debit' | 'credit';
  amount: number;
  currency: 'USD' | 'LRD';
  status: string;
  description: string;
  purpose: string;
  created_at: string;
  counterparty_name: string;
  counterparty_email: string;
  is_sender: boolean;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const TransactionHistory: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);

  const token = localStorage.getItem('merchant_token');

  // Fetch transactions from backend
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/merchants/wallet/transactions?limit=200`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setTransactions(data.data);
        setFilteredTransactions(data.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(tx => 
        tx.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.counterparty_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.counterparty_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter (debit/credit)
    if (filterType !== 'all') {
      filtered = filtered.filter(tx => tx.type === filterType);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(tx => tx.status === filterStatus);
    }

    // Date range filter
    if (dateRange.start) {
      filtered = filtered.filter(tx => new Date(tx.created_at) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      filtered = filtered.filter(tx => new Date(tx.created_at) <= new Date(dateRange.end));
    }

    setFilteredTransactions(filtered);
  }, [searchTerm, filterType, filterStatus, dateRange, transactions]);

  const getTypeIcon = (type: string, isSender: boolean) => {
    if (type === 'credit') {
      return <FaArrowDown className="type-icon incoming" />;
    } else {
      return <FaArrowUp className="type-icon outgoing" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed': return <FiCheckCircle />;
      case 'pending': return <FiClock />;
      case 'failed': return <FiXCircle />;
      default: return <FiAlertCircle />;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatCurrency = (amount: number, currency: 'USD' | 'LRD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Reference', 'Type', 'Amount', 'Currency', 'Status', 'Counterparty', 'Description', 'Purpose'];
    const csvRows = filteredTransactions.map(tx => [
      formatDate(tx.created_at),
      tx.reference,
      tx.type === 'credit' ? 'Received' : 'Sent',
      tx.amount,
      tx.currency,
      tx.status,
      tx.counterparty_name,
      tx.description,
      tx.purpose
    ]);
    
    const csvContent = [headers, ...csvRows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export to Excel (using CSV with .xls extension)
  const exportToExcel = () => {
    const headers = ['Date', 'Reference', 'Type', 'Amount', 'Currency', 'Status', 'Counterparty', 'Description', 'Purpose'];
    const excelRows = filteredTransactions.map(tx => [
      formatDate(tx.created_at),
      tx.reference,
      tx.type === 'credit' ? 'Received' : 'Sent',
      tx.amount,
      tx.currency,
      tx.status,
      tx.counterparty_name,
      tx.description,
      tx.purpose
    ]);
    
    const excelContent = [headers, ...excelRows].map(row => row.join('\t')).join('\n');
    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export to PDF (using window.print for simplicity)
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Transaction History</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #10b981; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #10b981; color: white; }
          .summary { margin-bottom: 20px; }
          .summary-item { display: inline-block; margin-right: 20px; padding: 10px; background: #f5f5f5; border-radius: 5px; }
        </style>
      </head>
      <body>
        <h1>Transaction History</h1>
        <div class="summary">
          <div class="summary-item">Total Transactions: ${filteredTransactions.length}</div>
          <div class="summary-item">Total Received: ${formatCurrency(filteredTransactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.amount, 0), 'USD')}</div>
          <div class="summary-item">Total Sent: ${formatCurrency(filteredTransactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.amount, 0), 'USD')}</div>
        </div>
        <table>
          <thead>
            <tr><th>Date</th><th>Reference</th><th>Type</th><th>Amount</th><th>Currency</th><th>Status</th><th>Counterparty</th><th>Description</th></tr>
          </thead>
          <tbody>
            ${filteredTransactions.map(tx => `
              <tr>
                <td>${formatDate(tx.created_at)}</td>
                <td>${tx.reference}</td>
                <td>${tx.type === 'credit' ? 'Received' : 'Sent'}</td>
                <td>${tx.amount}</td>
                <td>${tx.currency}</td>
                <td>${tx.status}</td>
                <td>${tx.counterparty_name}</td>
                <td>${tx.description || tx.purpose || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleExport = (format: string) => {
    setExporting(true);
    setTimeout(() => {
      if (format === 'csv') exportToCSV();
      if (format === 'excel') exportToExcel();
      if (format === 'pdf') exportToPDF();
      setExporting(false);
    }, 100);
  };

  const summary = {
    total_income: filteredTransactions
      .filter(tx => tx.type === 'credit')
      .reduce((sum, tx) => sum + (tx.currency === 'USD' ? tx.amount : tx.amount / 200), 0),
    total_expenses: filteredTransactions
      .filter(tx => tx.type === 'debit')
      .reduce((sum, tx) => sum + (tx.currency === 'USD' ? tx.amount : tx.amount / 200), 0),
    total_transactions: filteredTransactions.length
  };

  if (loading) {
    return (
      <div className="transaction-history">
        <div className="loading-transactions">
          <div className="spinner"></div>
          <p>Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-history">
      <div className="transaction-header">
        <h2>
          <FaExchangeAlt /> Transaction History
        </h2>
        <div className="export-buttons">
          <button className="export-btn csv" onClick={() => handleExport('csv')} disabled={exporting}>
            <FaFileCsv /> CSV
          </button>
          <button className="export-btn excel" onClick={() => handleExport('excel')} disabled={exporting}>
            <FaFileExcel /> Excel
          </button>
          <button className="export-btn pdf" onClick={() => handleExport('pdf')} disabled={exporting}>
            <FaFilePdf /> PDF
          </button>
          <button className="refresh-btn" onClick={fetchTransactions} disabled={loading}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      <div className="transaction-summary">
        <div className="summary-card">
          <span>Total Transactions</span>
          <h3>{summary.total_transactions}</h3>
        </div>
        <div className="summary-card income">
          <span>Total Received</span>
          <h3>{formatCurrency(summary.total_income, 'USD')}</h3>
        </div>
        <div className="summary-card expenses">
          <span>Total Sent</span>
          <h3>{formatCurrency(summary.total_expenses, 'USD')}</h3>
        </div>
      </div>

      <div className="transaction-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by reference, name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <FiFilter />
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="credit">Received</option>
            <option value="debit">Sent</option>
          </select>
        </div>
        <div className="filter-group">
          <FiClock />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="date-range">
          <FiCalendar />
          <input 
            type="date" 
            placeholder="Start" 
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
          />
          <span>-</span>
          <input 
            type="date" 
            placeholder="End" 
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
          />
        </div>
      </div>

      <div className="transactions-list">
        {filteredTransactions.length > 0 ? (
          filteredTransactions.map((tx) => (
            <div key={tx.id} className="transaction-item">
              <div className="transaction-icon">
                {getTypeIcon(tx.type, tx.is_sender)}
              </div>
              <div className="transaction-details">
                <div className="transaction-info">
                  <span className="transaction-reference">{tx.reference}</span>
                  <span className={`transaction-type ${tx.type}`}>
                    {tx.type === 'credit' ? 'RECEIVED' : 'SENT'}
                  </span>
                </div>
                <div className="transaction-parties">
                  <span>
                    {tx.type === 'credit' ? 'From: ' : 'To: '}
                    <strong>{tx.counterparty_name}</strong>
                  </span>
                  <span className="transaction-email">{tx.counterparty_email}</span>
                </div>
                <div className="transaction-description">{tx.description || tx.purpose}</div>
                <div className="transaction-date">{formatDate(tx.created_at)}</div>
              </div>
              <div className="transaction-amount-status">
                <div className={`transaction-amount ${tx.type}`}>
                  {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount, tx.currency)}
                </div>
                <div className="transaction-status" style={{ color: getStatusColor(tx.status) }}>
                  {getStatusIcon(tx.status)} {tx.status}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <FiAlertCircle size={48} />
            <p>No transactions found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
